import React, { useMemo } from "react";
import styled from "styled-components";
import {
  emailPreviewModalContainerTestId,
  emailPreviewModalContentTestId,
  emailPreviewModalFooterCloseButtonTestId,
  emailPreviewModalRecipientTestId,
  emailPreviewModalSubjectTestId
} from "../testIds";
import { useEmailReply } from "../context";
import { replaceStationeryBodyWithContentAndHead } from "../utils/replace-text-utils";
import {
  normalizeEmptyParagraphs,
  wrapMediaWidget
} from "@coxauto-ui/cxm/communications/util";
import parse from "html-react-parser";
import { FooterButtonContainer, StyledModal } from "./components/styled-modal";
import { Button } from "@interstate/components/Button";

interface EmailPreviewModalProps {
  /** The email HTML content to preview */
  htmlContent: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** The recipient email address */
  recipient: string;
  /** The email subject */
  subject: string;
  /** CC recipients */
  ccRecipients?: string[];
  /** BCC recipients */
  bccRecipients?: string[];
}

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: min(800px, 70vh);
  overflow: hidden;
`;

const EmailMetadata = styled.div`
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const EmailField = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailLabel = styled.span`
  font-weight: 600;
  color: #666;
  margin-right: 8px;
  min-width: 60px;
  flex-shrink: 0;
`;

const EmailValue = styled.span`
  color: #333;
  flex: 1;
`;

const EmailContent = styled.div`
  padding: 24px;
  background-color: white;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

export function EmailPreviewModal({
  htmlContent,
  isOpen,
  onClose,
  recipient,
  subject,
  ccRecipients,
  bccRecipients
}: EmailPreviewModalProps) {
  const { selectedStationeryHTML, templateHead } = useEmailReply();
  const previewHTML = useMemo(() => {
    const prepared = wrapMediaWidget(normalizeEmptyParagraphs(htmlContent));
    return replaceStationeryBodyWithContentAndHead(
      prepared,
      selectedStationeryHTML,
      templateHead
    );
  }, [selectedStationeryHTML, htmlContent, templateHead]);

  const modalContent = (
    <ModalContentWrapper>
      <EmailMetadata>
        <EmailField>
          <EmailLabel>To:</EmailLabel>
          <EmailValue data-testid={emailPreviewModalRecipientTestId}>
            {recipient || "No recipient specified"}
          </EmailValue>
        </EmailField>
        {ccRecipients && ccRecipients.length > 0 && (
          <EmailField>
            <EmailLabel>CC:</EmailLabel>
            <EmailValue data-testid="email-preview-modal-cc">
              {ccRecipients.filter(email => email.trim() !== "").join(", ")}
            </EmailValue>
          </EmailField>
        )}
        {bccRecipients && bccRecipients.length > 0 && (
          <EmailField>
            <EmailLabel>BCC:</EmailLabel>
            <EmailValue data-testid="email-preview-modal-bcc">
              {bccRecipients.filter(email => email.trim() !== "").join(", ")}
            </EmailValue>
          </EmailField>
        )}
        <EmailField>
          <EmailLabel>Subject:</EmailLabel>
          <EmailValue data-testid={emailPreviewModalSubjectTestId}>
            {subject || "No subject"}
          </EmailValue>
        </EmailField>
      </EmailMetadata>

      <EmailContent data-testid={emailPreviewModalContentTestId}>
        {previewHTML ? (
          <span style={{ fontWeight: "normal" }}>{parse(previewHTML)}</span>
        ) : (
          <p style={{ color: "#666", fontStyle: "italic" }}>
            No email content to preview
          </p>
        )}
      </EmailContent>
    </ModalContentWrapper>
  );

  const modalFooter = (
    <FooterButtonContainer>
      <Button
        buttonStyle="secondary"
        data-testid={emailPreviewModalFooterCloseButtonTestId}
        onClick={onClose}
      >
        Close
      </Button>
    </FooterButtonContainer>
  );

  return (
    <StyledModal
      containerTestId={emailPreviewModalContainerTestId}
      footer={modalFooter}
      isOpen={isOpen}
      title="Email Preview"
      onClose={onClose}
    >
      {modalContent}
    </StyledModal>
  );
}

export default EmailPreviewModal;
